import { useEffect, useRef, useState } from "preact/hooks";

import type { AssigneeOption } from "../components/AssigneePickerSheet";
import { CalendarCreateSheet } from "../components/CalendarCreateSheet";
import { CalendarDetailPopover } from "../components/CalendarDetailPopover";
import { CalendarFlexibleStrip } from "../components/CalendarFlexibleStrip";
import { CalendarOverdueBanner } from "../components/CalendarOverdueBanner";
import { CalendarRescheduleSheet } from "../components/CalendarRescheduleSheet";
import { InfoTooltip } from "../components/InfoTooltip";
import { Snackbar } from "../components/Snackbar";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useSchedule } from "../hooks/useSchedule";
import {
  buildWeekDays,
  monthLabel,
  parseISODate,
  toISODate,
  weekRangeLabel,
} from "../lib/calendarDates";
import { HELP_COPY } from "../lib/helpCopy";
import { monthDayLabel } from "../lib/recordsCopy";
import type { DueResponse, ScheduleOccurrence } from "../types";
import { CalendarMonthView } from "./CalendarMonthView";
import { CalendarPhoneAgenda } from "./CalendarPhoneAgenda";
import { CalendarWeekView } from "./CalendarWeekView";

export interface CalendarScreenProps {
  /** The signed-in person's own `GET /due?scope=mine` response — already
   * fetched by `app.tsx` for Today/My list; reused verbatim (no second
   * fetch) as the A5-R1 overdue banner's data source (§0's "reuse the
   * exact same hooks the phone app already has" posture, same one
   * `DashboardView.tsx` already established). */
  data: DueResponse;
  canHandOutWork: boolean;
  /** Manager/admin only — the roster options `CalendarCreateSheet`'s "Who
   * does it" picker needs, sourced from `app.tsx`'s already-fetched
   * `useTeamBoard` (no second fetch). Empty for a member (the create
   * affordance never renders for that role, so this is unused). */
  roster: AssigneeOption[];
  onNavigate: (path: string) => void;
  onBack: () => void;
}

/**
 * Sprint-009 B4 — `/calendar` (design-phase-b.md §2, ADR-002 Amendment 5
 * decisions 1-3). Desktop: month `<table>` or week headings/lists, per
 * `useSchedule`'s `viewMode`. Phone: week strip + agenda only — NO month
 * grid ever renders below the desktop breakpoint (§0's own decision).
 * Same routed-screen shape as `AnimalsScreen.tsx` (desktop bare `<h1>`,
 * phone `.more-subheader` "‹ Back to More").
 */
export function CalendarScreen({
  data,
  canHandOutWork,
  roster,
  onNavigate,
  onBack,
}: CalendarScreenProps) {
  const isDesktop = useIsDesktop();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useFocusHeadingOnMount(headingRef);

  const schedule = useSchedule({ canHandOutWork, navigate: onNavigate });
  const {
    viewMode,
    setViewMode,
    anchor,
    setAnchor,
    scope,
    setScope,
    canToggleScope,
    occurrences,
    flexible,
    loading,
    refreshing,
    error,
    snackbar,
    dismissSnackbar,
    assignmentDateById,
    movedFromByAssignmentId,
    thisWeekDays,
    isOnToday,
    goToday,
    goPrev,
    goNext,
    createOneOff,
    moveOneOff,
    moveOccurrence,
    skipOccurrence,
  } = schedule;

  // §2.4/§0 — phone never shows a month grid; force week mode below the
  // desktop breakpoint regardless of whatever the last desktop session
  // left `viewMode` at.
  useEffect(() => {
    if (!isDesktop) setViewMode("week");
  }, [isDesktop, setViewMode]);

  const [selectedDate, setSelectedDate] = useState<Date>(anchor);
  useEffect(() => {
    setSelectedDate(anchor);
  }, [anchor]);

  const [detailOccurrence, setDetailOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [rescheduleOccurrence, setRescheduleOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [createDateIso, setCreateDateIso] = useState<string | null>(null);

  // §2.5/§2.10/§2.10a/§2.11 — one tap-dispatch table shared by every
  // surface (month/week/phone), so the role/status rules are enforced in
  // exactly one place rather than re-derived per view.
  function handleTapOccurrence(occ: ScheduleOccurrence) {
    if (occ.kind === "health_event") {
      onNavigate("/animals");
      return;
    }
    if (occ.status === "done") {
      setDetailOccurrence(occ);
      return;
    }
    if (!canHandOutWork) {
      // Member: every non-done chip (live/skipped/moved alike) resolves
      // to the same read-only popover, per §2.11's own table.
      setDetailOccurrence(occ);
      return;
    }
    if (occ.exception?.action === "moved") {
      // Writer: a moved ghost chip is a real navigation to the linked
      // one-off's new date, not a sheet (§2.5).
      const targetDate = occ.exception.linked_assignment_id
        ? assignmentDateById.get(occ.exception.linked_assignment_id)
        : undefined;
      if (targetDate) {
        const targetAnchor = parseISODate(targetDate);
        setAnchor(targetAnchor);
        setSelectedDate(targetAnchor);
      }
      return;
    }
    if (occ.exception?.action === "suppressed") {
      // Writer: skipped chip -> read-only detail + Undo (§2.5).
      setDetailOccurrence(occ);
      return;
    }
    setRescheduleOccurrence(occ);
  }

  const weekDays = buildWeekDays(anchor);
  const scopeLabel = scope === "team" ? "Team calendar" : "My calendar";
  const subheading = viewMode === "month" ? monthLabel(anchor) : weekRangeLabel(weekDays);

  return (
    <section class="view calendar-view" aria-label="Calendar">
      {isDesktop ? (
        <header class="page-header">
          <h1 ref={headingRef} tabIndex={-1} class="view-heading">
            Calendar
            <InfoTooltip
              label={HELP_COPY.calendarMovedSkipped.label}
              content={HELP_COPY.calendarMovedSkipped.content}
            />
          </h1>
          <p class="view-subheading">{subheading}</p>
        </header>
      ) : (
        <>
          <div class="more-subheader">
            <button type="button" class="icon-btn" aria-label="Back to More" onClick={onBack}>
              ‹
            </button>
            <h1 ref={headingRef} tabIndex={-1} class="view-heading">
              Calendar
              <InfoTooltip
                label={HELP_COPY.calendarMovedSkipped.label}
                content={HELP_COPY.calendarMovedSkipped.content}
              />
            </h1>
          </div>
          <p class="view-subheading">{monthDayLabel(toISODate(selectedDate))}</p>
        </>
      )}

      <div class="calendar-toolbar">
        {isDesktop ? (
          <div class="calendar-toolbar-group">
            <div class="calendar-nav">
              <button type="button" class="calendar-nav-btn" aria-label="Previous" onClick={goPrev}>
                ‹
              </button>
              <button type="button" class="calendar-nav-btn" disabled={isOnToday} onClick={goToday}>
                Today
              </button>
              <button type="button" class="calendar-nav-btn" aria-label="Next" onClick={goNext}>
                ›
              </button>
            </div>
            <span class="calendar-toolbar-divider" aria-hidden="true" />
            <div class="calendar-view-toggle" role="group" aria-label="Calendar view">
              <button
                type="button"
                aria-pressed={viewMode === "month"}
                onClick={() => setViewMode("month")}
              >
                Month
              </button>
              <button
                type="button"
                aria-pressed={viewMode === "week"}
                onClick={() => setViewMode("week")}
              >
                Week
              </button>
            </div>
          </div>
        ) : null}
        {canToggleScope ? (
          <div class="calendar-scope-toggle" role="group" aria-label="Whose calendar">
            <button type="button" aria-pressed={scope === "mine"} onClick={() => setScope("mine")}>
              My calendar
            </button>
            <button type="button" aria-pressed={scope === "team"} onClick={() => setScope("team")}>
              Team calendar
            </button>
          </div>
        ) : null}
      </div>

      <CalendarFlexibleStrip items={flexible} />

      <CalendarOverdueBanner items={data.overdue} onOpenTask={() => onNavigate("/today")} />

      {/* TD-010 fix (design-phase-c.md §8, binding for every view) — a
       * write's own refetch must never swap this already-mounted subtree
       * for a full-panel placeholder. `loading` is true ONLY on the true
       * first paint (nothing has ever loaded, nothing to preserve yet);
       * every subsequent refresh (create/reschedule/skip/undo, or Quick
       * Add landing back here) renders `refreshing`'s small, non-blocking
       * hint ALONGSIDE the still-mounted month/week/agenda view instead —
       * `CalendarMonthView`'s own `expandedDays` "+N more" state (and any
       * scroll position/open popover) survives untouched. */}
      {loading ? (
        error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : (
          <p class="loading-hint">Loading the calendar…</p>
        )
      ) : (
        <>
          {refreshing ? (
            <p class="loading-hint calendar-refresh-hint" aria-live="polite">
              Updating…
            </p>
          ) : null}
          {error ? (
            <p class="error-hint" role="alert">
              {error}
            </p>
          ) : null}
          {isDesktop ? (
            viewMode === "month" ? (
              <CalendarMonthView
                anchor={anchor}
                occurrences={occurrences}
                assignmentDateById={assignmentDateById}
                movedFromByAssignmentId={movedFromByAssignmentId}
                thisWeekDays={thisWeekDays}
                scopeLabel={scopeLabel}
                canWrite={canHandOutWork}
                onTapOccurrence={handleTapOccurrence}
                onAddForDay={setCreateDateIso}
              />
            ) : (
              <CalendarWeekView
                anchor={anchor}
                occurrences={occurrences}
                assignmentDateById={assignmentDateById}
                movedFromByAssignmentId={movedFromByAssignmentId}
                thisWeekDays={thisWeekDays}
                canWrite={canHandOutWork}
                onTapOccurrence={handleTapOccurrence}
                onAddForDay={setCreateDateIso}
              />
            )
          ) : (
            <CalendarPhoneAgenda
              weekDays={weekDays}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              occurrences={occurrences}
              assignmentDateById={assignmentDateById}
              movedFromByAssignmentId={movedFromByAssignmentId}
              thisWeekDays={thisWeekDays}
              canWrite={canHandOutWork}
              onTapOccurrence={handleTapOccurrence}
              onAddForDay={setCreateDateIso}
              onPrevWeek={goPrev}
              onNextWeek={goNext}
            />
          )}
        </>
      )}

      <CalendarDetailPopover
        occurrence={detailOccurrence}
        isReadOnlyViewer={!canHandOutWork}
        onUndo={
          canHandOutWork &&
          detailOccurrence?.exception?.action === "suppressed" &&
          detailOccurrence.cadence_id
            ? () => {
                const cadenceId = detailOccurrence.cadence_id as string;
                const occurrenceDate = detailOccurrence.date;
                setDetailOccurrence(null);
                void schedule.undoException(cadenceId, occurrenceDate);
              }
            : undefined
        }
        onClose={() => setDetailOccurrence(null)}
      />

      <CalendarRescheduleSheet
        occurrence={rescheduleOccurrence}
        onCancel={() => setRescheduleOccurrence(null)}
        onMoveCadence={moveOccurrence}
        onSkipCadence={skipOccurrence}
        onMoveOneOff={moveOneOff}
      />

      <CalendarCreateSheet
        open={createDateIso !== null}
        dateLabel={createDateIso ? monthDayLabel(createDateIso) : ""}
        roster={roster}
        onAdd={(input) => createOneOff({ ...input, dueDateIso: createDateIso as string })}
        onCancel={() => setCreateDateIso(null)}
      />

      <Snackbar state={snackbar} onDismiss={dismissSnackbar} />
    </section>
  );
}
